import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useEffect, useRef, useState } from "react";

import { blurActiveElement } from "@/utils/blur";

type ProductResultsContextValue = {
    showFacets: boolean;
    mobileFacetsOpen: boolean;
    setMobileFacetsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    stickyFiltersRef: React.RefObject<HTMLDivElement | null>;
    filtersRef: React.RefObject<HTMLDivElement | null>;
};

type ProductResultsContextProviderProps = RequireChildrenFor<{
    showFacets: boolean;
}>;

export const ProductResultsContext = createContext<
    ProductResultsContextValue | undefined
>(undefined);
ProductResultsContext.displayName = "ProductResultsContext";

export const ProductResultsContextProvider = ({
    showFacets,
    children
}: ProductResultsContextProviderProps) => {
    const [mobileFacetsOpen, setMobileFacetsOpen] = useState(false);
    const filtersRef = useRef<HTMLDivElement>(null);
    const stickyFiltersRef = useRef<HTMLDivElement>(null);
    const stickFilters = useRef<boolean>(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const handleScroll = () => {
            if (!filtersRef.current) return;
            if (!stickyFiltersRef.current) return;

            const filtersY = filtersRef.current?.getBoundingClientRect().y;

            const stickFiltersToTop = isDefined(filtersY) && filtersY <= 0;

            if (stickFilters.current !== stickFiltersToTop) {
                if (stickFiltersToTop) {
                    blurActiveElement();
                    stickyFiltersRef.current.classList.remove("tw:hidden");
                    stickyFiltersRef.current.classList.add("tw:grid");
                } else {
                    stickyFiltersRef.current.classList.remove("tw:grid");
                    stickyFiltersRef.current.classList.add("tw:hidden");
                }

                stickFilters.current = stickFiltersToTop;
            }
        };

        document.addEventListener("scroll", handleScroll);

        return () => {
            document.removeEventListener("scroll", handleScroll);
        };
    }, [filtersRef, stickFilters]);

    return (
        <ProductResultsContext
            value={{
                showFacets,
                mobileFacetsOpen,
                setMobileFacetsOpen,
                stickyFiltersRef,
                filtersRef
            }}
        >
            {children}
        </ProductResultsContext>
    );
};

export const useProductResultsContext = () => {
    const context = use<ProductResultsContextValue | undefined>(
        ProductResultsContext
    );

    if (!isDefined(context)) {
        throw new Error(`${ProductResultsContext.displayName} is undefined.`);
    }

    return context;
};
